/**
 * ContextSectionsBlock — bloque "Contexto interno (auto)" del Designer
 * (Doc 24 §3 Fase 8).
 *
 * Sustituye al panel inline `ContextSectionsPanel` (B.6.d.2.b — v1) que
 * solo soportaba refs intra sin enforzar §3.3.2. Esta versión:
 *
 *   - Enforza la regla §3.3.2 — solo permite agregar secciones anteriores
 *     en el documento que no contienen a la activa (el cálculo lo hace el
 *     padre y pasa `availableRefs.intra` ya filtrado).
 *   - Maneja refs cross-template (`tpl-x.foo`) como placeholders
 *     visuales — la población real (Sección "De otros documentos del
 *     proyecto") llega en ADR-0002 Fase 10.f. Ver §1.8 del Doc 24.
 *   - Empty states diferenciados según haya o no secciones anteriores
 *     referenciables.
 *
 * El componente es puramente presentacional / controlled — no toca
 * `DesignerState`. El padre (`SectionEditorPane`) calcula
 * `availableRefs.intra` desde `state.definition` y wirea
 * `onAdd` / `onRemove` al dispatch del reducer.
 */

import { ChevronDown, Link2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RefChip, type RefState } from './RefChip'
import type { SupportedLocale } from '../../../i18n/config'

/** Sección intra disponible para referenciar (ya filtrada por el padre
 * con la regla §3.3.2). */
export interface AvailableIntraRef {
  section_id: string
  title: { es: string; en: string }
  type: string
}

/** Documento cross-template con sus secciones — TV2 siempre llega como
 * `[]`; ADR-0002 popula con los templates del proyecto. */
export interface AvailableCrossDoc {
  template_key: string
  template_name: { es: string; en: string }
  sections: {
    section_id: string
    title: { es: string; en: string }
  }[]
}

export interface ContextSectionsBlockProps {
  /** Locale activo del designer (para títulos bilingües). */
  locale: SupportedLocale
  /** Refs ya declaradas en `context_sections[]` de la sección activa. */
  currentRefs: string[]
  /** Opciones disponibles.
   * `intra` contiene TODAS las secciones intra eligibles (filtradas por el
   * padre con regla §3.3.2 — anteriores en el documento que no contienen
   * a la activa, sin `fixed_block`). El componente filtra internamente las que
   * ya están en `currentRefs` para el dropdown, pero usa la lista
   * completa para hidratar los títulos de los chips.
   * `cross` siempre llega como `[]` en TV2 — ADR-0002 lo popula. */
  availableRefs: {
    intra: AvailableIntraRef[]
    cross: AvailableCrossDoc[]
  }
  readOnly?: boolean
  onAdd(ref: string): void
  onRemove(ref: string): void
}

/** Determina el `RefState` de un ref guardado. En el designer (sin
 * proyecto) los cross-refs declarados se marcan como `cross_canon`
 * (estado neutro/declarativo) — el render del chip aún muestra info
 * legible del template + sección. Los sub-estados reales
 * (canon/draft/missing/empty) se resuelven cuando el chip se reusa en
 * el editor del artifact dentro de un proyecto. ADR-0002 Fase 10.f.1. */
function classifyRef(ref: string): RefState {
  return ref.includes('.') ? 'cross_canon' : 'intra'
}


export function ContextSectionsBlock({
  locale,
  currentRefs,
  availableRefs,
  readOnly = false,
  onAdd,
  onRemove,
}: ContextSectionsBlockProps) {
  const { t } = useTranslation('templates')
  const [pickerOpen, setPickerOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Cerrar dropdown al click fuera.
  useEffect(() => {
    if (!pickerOpen) return
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setPickerOpen(false)
      }
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [pickerOpen])

  // Dropdown solo muestra opciones aún no referenciadas.
  const selectedSet = new Set(currentRefs)
  const dropdownIntraOptions = availableRefs.intra.filter(
    (r) => !selectedSet.has(r.section_id),
  )
  const hasIntraOptions = dropdownIntraOptions.length > 0
  const hasNoPreviousSections = availableRefs.intra.length === 0

  // Mapa para hidratar título intra al renderear chip — contiene TODAS
  // las eligibles, incluyendo las ya seleccionadas.
  const intraTitleById = new Map<string, { es: string; en: string }>()
  for (const r of availableRefs.intra) {
    intraTitleById.set(r.section_id, r.title)
  }

  // ADR-0002 Fase 10.f.1 — opciones cross-template del catálogo.
  // Cada doc expone N secciones; aplanamos a entries `tpl-x.section_id`
  // y excluimos las ya referenciadas.
  const crossOptions = availableRefs.cross.flatMap((doc) =>
    doc.sections.map((s) => ({
      ref: `${doc.template_key}.${s.section_id}`,
      templateName: doc.template_name,
      sectionTitle: s.title,
    })),
  )
  const dropdownCrossOptions = crossOptions.filter(
    (o) => !selectedSet.has(o.ref),
  )
  const hasCrossOptions = dropdownCrossOptions.length > 0

  // Mapa para hidratar el chip cross con el template + sección legibles.
  const crossSourceByRef = new Map<
    string,
    {
      templateName: { es: string; en: string }
      sectionTitle: { es: string; en: string }
    }
  >()
  for (const o of crossOptions) {
    crossSourceByRef.set(o.ref, {
      templateName: o.templateName,
      sectionTitle: o.sectionTitle,
    })
  }

  function handleAdd(ref: string) {
    onAdd(ref)
    setPickerOpen(false)
  }

  return (
    <div
      className="rounded-md border border-line bg-surface-muted/40 p-3"
      data-testid="context-sections-block"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-medium text-ink-muted">
          <Link2 className="w-3.5 h-3.5 text-brand" />
          {t('designer.editor.context_sections.block_title')}
        </div>
        {!readOnly ? (
          <div ref={containerRef} className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11px] text-ink-muted hover:border-brand/40 hover:bg-brand/5 hover:text-ink"
              data-testid="context-sections-add-button"
            >
              {t('designer.editor.context_sections.add_reference')}
              <ChevronDown className="w-3 h-3" />
            </button>
            {pickerOpen ? (
              <div
                className="absolute right-0 z-10 mt-1 w-80 max-h-72 overflow-hidden rounded-md border border-line bg-surface shadow-lg flex flex-col"
                data-testid="context-sections-dropdown"
              >
                {/* Sección "Del mismo documento" */}
                <div className="border-b border-line bg-surface-muted/40 px-3 py-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium">
                    {t('designer.editor.context_sections.same_document_label')}
                  </p>
                </div>
                <ul
                  className="overflow-y-auto py-1"
                  data-testid="context-sections-intra-list"
                >
                  {hasIntraOptions ? (
                    dropdownIntraOptions.map((r) => (
                      <li key={r.section_id}>
                        <button
                          type="button"
                          onClick={() => handleAdd(r.section_id)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-surface-muted"
                          data-testid={`context-sections-intra-option-${r.section_id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-ink truncate">
                              {r.title[locale] || r.title['es'] || r.section_id}
                            </span>
                          </div>
                          <p className="font-mono text-[10px] text-ink-muted truncate">
                            {r.section_id}
                          </p>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-[11px] text-ink-subtle">
                      {hasNoPreviousSections
                        ? t(
                            'designer.editor.context_sections.no_previous_sections',
                          )
                        : t('designer.editor.context_sections.empty_state')}
                    </li>
                  )}
                </ul>
                {/* Sección "De otros documentos del proyecto" — ADR-0002 Fase 10.f.1 */}
                <div className="border-t border-line bg-surface-muted/40 px-3 py-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium">
                    {t('designer.editor.context_sections.other_documents_label')}
                  </p>
                </div>
                {hasCrossOptions ? (
                  <ul
                    className="overflow-y-auto py-1 max-h-40"
                    data-testid="context-sections-cross-list"
                  >
                    {dropdownCrossOptions.map((opt) => (
                      <li key={opt.ref}>
                        <button
                          type="button"
                          onClick={() => handleAdd(opt.ref)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-surface-muted"
                          data-testid={`context-sections-cross-option-${opt.ref}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-ink truncate">
                              {opt.templateName[locale] ||
                                opt.templateName['es']}
                            </span>
                            <span className="text-ink-subtle">·</span>
                            <span className="text-ink truncate">
                              {opt.sectionTitle[locale] ||
                                opt.sectionTitle['es']}
                            </span>
                          </div>
                          <p className="font-mono text-[10px] text-ink-muted truncate">
                            {opt.ref}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div
                    className="px-3 py-2 text-[11px] text-ink-subtle italic"
                    data-testid="context-sections-cross-empty"
                  >
                    {t(
                      'designer.editor.context_sections.other_documents_empty',
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <p className="mt-1 text-[11px] text-ink-subtle italic">
        {t('designer.editor.context_sections.helper')}
      </p>

      {currentRefs.length === 0 ? (
        <p
          className="mt-2 text-[11px] text-ink-subtle"
          data-testid="context-sections-empty"
        >
          {hasNoPreviousSections
            ? t('designer.editor.context_sections.no_previous_sections')
            : t('designer.editor.context_sections.empty_state')}
        </p>
      ) : (
        <ul
          className="mt-2 flex flex-wrap gap-1.5"
          data-testid="context-sections-chips"
        >
          {currentRefs.map((refValue) => {
            const state = classifyRef(refValue)
            const title = intraTitleById.get(refValue)
            const crossSource = crossSourceByRef.get(refValue)
            return (
              <RefChip
                key={refValue}
                refValue={refValue}
                state={state}
                locale={locale}
                {...(title ? { title } : {})}
                {...(crossSource ? { crossSource } : {})}
                readOnly={readOnly}
                onRemove={() => onRemove(refValue)}
                testId={`context-sections-chip-${refValue}`}
              />
            )
          })}
        </ul>
      )}
    </div>
  )
}
